import { createElement } from 'react';
import { toast } from 'sonner';

interface ApiErrorResponse {
  errorMessage?: string;
  error?: string;
  message?: string;
  metadata?: Record<string, unknown>;
  response?: {
    data?: ApiErrorResponse;
  };
}

interface ErrorToastConfig {
  message: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export function showErrorToast(error: unknown): void {
  const formatted = formatErrorMessage(error);

  if (typeof formatted === 'string') {
    toast.error(formatted, {
      duration: 5000,
      style: {
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      },
    });
  } else {
    const descriptionElement = formatted.description
      ? createElement(
          'div',
          { style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.5' } },
          ...formatted.description.split('\n').flatMap((line, i, arr) => {
            const elements: Array<string | React.ReactElement> = [line];
            if (i < arr.length - 1) {
              elements.push(createElement('br', { key: `br-${i}` }));
            }

            return elements;
          }),
        )
      : undefined;

    toast.error(formatted.message, {
      description: descriptionElement,
      action: formatted.action,
      duration: 8000,
      style: {
        maxWidth: '550px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      },
    });
  }
}

export function formatErrorMessage(error: unknown): string | ErrorToastConfig {
  const fullMessage = extractFullMessage(error);
  const { mainMessage, metadata } = parseErrorMessage(error);

  if (!metadata || Object.keys(metadata).length === 0) {
    return mainMessage;
  }

  const metadataEntries = Object.entries(metadata)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => formatMetadataEntry(key, value))
    .filter(entry => entry.length > 0);

  if (metadataEntries.length === 0) {
    return mainMessage;
  }

  const description = metadataEntries.join('\n');

  return {
    message: mainMessage,
    description,
    action: {
      label: 'Copy',
      onClick() {
        navigator.clipboard.writeText(fullMessage);
      },
    },
  };
}

function extractFullMessage(error: unknown): string {
  if (error instanceof Error) {
    const apiError = error as Error & { response?: { data?: ApiErrorResponse } };
    const errorData = apiError.response?.data;

    if (errorData) {
      return formatApiErrorAsText(errorData);
    }

    return error.message || 'An unknown error occurred';
  }

  if (typeof error === 'object' && error !== null) {
    return formatApiErrorAsText(error as ApiErrorResponse);
  }

  return String(error) || 'An unknown error occurred';
}

function parseErrorMessage(error: unknown): {
  mainMessage: string;
  metadata?: Record<string, unknown>;
} {
  if (error instanceof Error) {
    const apiError = error as Error & { response?: { data?: ApiErrorResponse } };
    const errorData = apiError.response?.data;

    if (errorData) {
      return {
        mainMessage:
          errorData.errorMessage || errorData.error || errorData.message || 'Operation failed',
        metadata: errorData.metadata,
      };
    }

    return { mainMessage: error.message || 'An unknown error occurred' };
  }

  if (typeof error === 'object' && error !== null) {
    const errorData = error as ApiErrorResponse;
    return {
      mainMessage:
        errorData.errorMessage || errorData.error || errorData.message || 'Operation failed',
      metadata: errorData.metadata,
    };
  }

  return { mainMessage: String(error) || 'An unknown error occurred' };
}

function formatApiErrorAsText(errorData: ApiErrorResponse): string {
  const mainMessage =
    errorData.errorMessage || errorData.error || errorData.message || 'Operation failed';

  if (errorData.metadata && Object.keys(errorData.metadata).length > 0) {
    const metadataEntries = Object.entries(errorData.metadata)
      .filter(([_, value]) => value !== null && value !== undefined)
      .map(([key, value]) => formatMetadataEntry(key, value))
      .filter(entry => entry.length > 0);

    if (metadataEntries.length > 0) {
      return `${mainMessage}\n\n${metadataEntries.join('\n')}`;
    }
  }

  return mainMessage;
}

function formatMetadataEntry(key: string, value: unknown): string {
  const formattedKey = formatKey(key);

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '';
    }

    if (value.length === 1) {
      return `${formattedKey}: ${formatValue(value[0])}`;
    }

    return `${formattedKey}:\n${value.map(v => `  • ${formatValue(v)}`).join('\n')}`;
  }

  if (typeof value === 'object' && value !== null) {
    const objEntries = Object.entries(value)
      .map(([k, v]) => `  ${formatKey(k)}: ${formatValue(v)}`)
      .join('\n');
    return `${formattedKey}:\n${objEntries}`;
  }

  return `${formattedKey}: ${formatValue(value)}`;
}

function formatKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .replace(/_/g, ' ')
    .trim();
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
}
